<template>
  <div>
    <!-- 软件类型选择器 -->
    <div class="software-type-selector">
      <div class="selector-container">
        <button class="type-btn" :class="{active: currentSoftwareType === 'mrt'}" @click="selectSoftwareType('mrt')">{{ $t('download.SoftwareDownload') }}</button>
        <button class="type-btn" :class="{active: currentSoftwareType === 'terminal'}" @click="selectSoftwareType('terminal')">{{ $t('download.TerminalToolsDownload') }}</button>
      </div>
    </div>
    
    <div class="container">
      <!-- 当前版本下载 -->
      <section class="current-download-section" v-if="currentSoftwareType === 'mrt'">
        <div class="download-card">
          <div class="download-layout">
            <!-- 左侧：标题 + 下载按钮 -->
            <div class="left-panel">
              <h1 class="software-title">{{ $t('common.MRTDataRecoveryTool') }}</h1>

              <!-- 自定义下载按钮 -->
              <div v-if="latestVersion" class="custom-download-btn">
                <button
                  type="button"
                  :class="{ 'has-dropdown': hasSubVersions(latestVersion) }"
                  @click="handleDownloadClick(latestVersion)"
                >
                  {{ $t('common.download') }} {{ latestVersion }}
                </button>

                <!-- 子版本下拉菜单 -->
                <ul v-if="showDropdown && hasSubVersions(latestVersion)" class="dropdown-menu">
                  <li
                    v-for="opt in multiVersionMap[latestVersion]"
                    :key="opt.id"
                    @click="handleDownloadDefaultWithOption(latestVersion, opt)"
                  >
                    {{ opt.name }}
                  </li>
                </ul>
              </div>

              <el-button v-else disabled type="info" class="download-btn">{{ $t('common.download') }} Null</el-button>
            </div>

            <!-- 右侧：升级日志 -->
            <div class="right-panel">
              <h2 class="changelog-title">{{ $t('common.upgradeLog') }}</h2>
              <div class="changelog-content" v-if="selectedChangelog">
                <div class="changelog-content-scrollable" v-html="selectedChangelog"></div>
              </div>
              <div v-else class="no-changelog">{{ $t('common.noUpgradeLog') }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- 历史版本下载 -->
      <section class="history-section" v-if="historyVersions.length > 0 && currentSoftwareType === 'mrt'">
        <h2 class="section-title">{{ $t('common.historicalVersionsDownload') }}</h2>
        <div class="software-content">
          <div
            class="driver-card"
            v-for="(version, index) in historyVersions"
            :key="index"
          >
            <div class="driver-header">
              <div class="driver-info">
                <h3 class="driver-title">{{ $t('common.MRTDataRecoveryTool') }} {{ version }}</h3>
              </div>
            </div>
            <div class="driver-download-area">
                <!-- 根据版本号区分下载按钮 -->
                <div class="custom-download-btn" v-if="hasSubVersions(version)">
                  <button type="button" @click="handleDownloadWithOption(version, {id: '', name: '单机版', suffix: ''})">单机版</button>
                  <button type="button" @click="handleDownloadWithOption(version, {id: 'net', name: '网络版', suffix: 'net'})">网络版</button>
                </div>
                <div class="custom-download-btn" v-else>
                  <button type="button" @click="downloadFile(version)">下载软件</button>
                </div>
              </div>
              
              <!-- 升级日志下拉显示区域 -->
              <div class="driver-description">
                <div class="changelog-toggle" @click="toggleChangelog(version)">
                  <span>{{ isChangelogVisible[version] ? $t('download.CollapseChangelog') : $t('download.ExpandChangelog') }}</span>
                  <span class="toggle-icon">{{ isChangelogVisible[version] ? '▼' : '►' }}</span>
                </div>
                <div class="changelog-container" :class="{ 'expanded': isChangelogVisible[version] }">
                  <div class="changelog-content-scrollable" v-html="changelogMap[version] || $t('common.noUpgradeLog')"></div>
                </div>
              </div>
          </div>
        </div>
      </section>
      <section v-else-if="currentSoftwareType === 'mrt'" class="history-section">
        <p class="no-history">{{ $t('common.noHistoricalVersions') }}</p>
      </section>
      
      <!-- 辅助工具下载内容 -->
      <section class="software-content" v-if="currentSoftwareType === 'terminal'">
        <!-- 驱动下载项 1 -->
        <div class="driver-card driver-card-pl2303">
          <div class="driver-header">
            <div class="driver-info">
              <h3 class="driver-title">{{ $t('download.PL2303Driver') }}</h3>
              <p class="driver-subtitle">{{ $t('download.PL2303Subtitle') }}</p>
            </div>
          </div>
          <div class="driver-download-area">
            <div class="custom-download-btn">
              <button type="button" @click="downloadDriver('PL2303')">{{ $t('download.DownloadDriver') }}</button>
            </div>
          </div>
          <div class="driver-description">
            <p>{{ $t('download.PL2303Desc') }}</p>
          </div>
        </div>
        
        <!-- 驱动下载项 2 -->
        <div class="driver-card driver-card-cp210x">
          <div class="driver-header">
            <div class="driver-info">
              <h3 class="driver-title">{{ $t('download.CP210xDriver') }}</h3>
              <p class="driver-subtitle">{{ $t('download.CP210xSubtitle') }}</p>
            </div>
          </div>
          <div class="driver-download-area">
            <div class="custom-download-btn">
              <button type="button" @click="downloadDriver('CP210x', 'WinXP')">{{ $t('download.ForWinXP') }}</button>
              <button type="button" @click="downloadDriver('CP210x', 'Win7+')">{{ $t('download.ForWin7') }}</button>
            </div>
          </div>
          <div class="driver-description">
            <p>{{ $t('download.CP210xDesc') }}</p>
          </div>
        </div>
        
        <!-- 驱动下载项 3 -->
        <div class="driver-card driver-card-sata">
          <div class="driver-header">
            <div class="driver-info">
              <h3 class="driver-title">{{ $t('download.SATADriver') }}</h3>
            </div>
          </div>
          <div class="driver-download-area">
            <div class="custom-download-btn">
              <button type="button" @click="downloadDriver('SATA', 'CH341SER')">{{ $t('download.CH341SERDriver') }}</button>
              <button type="button" @click="downloadDriver('SATA', 'MRTChangeSignal')">{{ $t('download.MRTChangeSignal') }}</button>
            </div>
          </div>
          <div class="driver-description">
            <p>{{ $t('download.SATADesc') }}</p>
          </div>
        </div>
      </section>
    </div>

    <!-- 弹窗 -->
    <div class="overlay" v-if="expandedVersion && currentSoftwareType === 'mrt'" @click.self="closeExpandedCard">
      <div class="overlay-content">
        <span class="close-btn" @click="closeExpandedCard">&times;</span>
        <div class="overlay-download-content">
          <!-- 左侧下载 -->
          <div class="left-panel">
            <h1 class="software-title">{{ $t('common.MRTDataRecoveryTool') }}</h1>

            <!-- 自定义下载按钮 -->
            <div class="custom-download-btn">
              <button
                type="button"
                :class="{ 'has-dropdown': hasSubVersions(expandedVersion) }"
                @click="handleExpandedDownloadClick(expandedVersion)"
              >
                {{ $t('common.download') }} {{ expandedVersion }}
              </button>

              <ul v-if="expandedShowDropdown && hasSubVersions(expandedVersion)" class="dropdown-menu">
                <li
                  v-for="opt in multiVersionMap[expandedVersion]"
                  :key="opt.id"
                  @click="handleExpandedDownloadWithOption(expandedVersion, opt)"
                >
                  {{ opt.name }}
                </li>
              </ul>
            </div>
          </div>

          <!-- 右侧日志 -->
          <div class="right-panel">
            <h2 class="changelog-title">{{ expandedVersion }} {{ $t('common.upgradeLog') }}</h2>
            <div class="changelog-content" v-if="expandedChangelog">
              <div v-html="expandedChangelog"></div>
            </div>
            <div v-else class="no-changelog">{{ $t('common.noUpgradeLog') }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import i18n from '../common/lang/index.js'
export default {
  name: 'SoftwareList',
  props: ['url'],
  data() {
    return {
      changelogUrl: '/changelog/update.txt',
      enChangelogUrl: '/changelog/update_en.txt',
      downloadBaseUrl: '/download/',
      selectedVersion: null,
      selectedChangelog: '',
      expandedVersion: null,
      expandedChangelog: '',
      allVersions: [],
      changelogMap: {},
      multiVersionMap: {},
      currentSoftwareType: 'mrt', // 默认显示MRT软件

      // 控制下拉显示
      showDropdown: false,
      expandedShowDropdown: false,
      
      // 控制升级日志展开收起
      isChangelogVisible: {}
    };
  },
  computed: {
    latestVersion() {
      return this.allVersions[0] || null;
    },
    historyVersions() {
      return this.allVersions.slice(1);
    }
  },
  created() {
    this.fetchChangelog();
  },
  methods: {
    // 选择软件类型
    selectSoftwareType(type) {
      this.currentSoftwareType = type
    },
    
    async fetchChangelog() {
      try {
        // 根据当前语言选择对应的更新日志文件
        const isEnglish = i18n.locale === 'en';
        const url = isEnglish ? this.enChangelogUrl : this.changelogUrl;
        const res = await fetch(url);
        if (!res.ok) throw new Error(isEnglish ? 'Failed to load changelog' : '无法加载升级日志');
        const text = await res.text();

        const { versions, logMap, multiVersionMap } = this.parseChangelog(text);

        this.allVersions = versions;
        this.changelogMap = logMap;
        this.multiVersionMap = multiVersionMap;

        if (versions.length > 0) {
          this.selectedVersion = versions[0];
          this.selectedChangelog = logMap[this.selectedVersion] || '';
        } else {
          this.selectedVersion = null;
          this.selectedChangelog = '';
        }
      } catch (e) {
        console.error(e);
        this.selectedChangelog = i18n.locale === 'en' ? 'Failed to load changelog. Please check file format or path.' : '升级日志加载失败，请检查文件格式或路径';
        this.allVersions = [];
      }
    },

    parseChangelog(text) {
      const lines = text.replace(/\r\n/g, '\n').split('\n');
      const result = {};
      let currentVersion = '';
      let currentLog = '';
      const versionOrder = [];
      const multiVersionMap = {};

      lines.forEach(line => {
        const h3Match = line.match(/<h3>(.*?)<\/h3>/i);
        if (h3Match) {
          const versionMatch = h3Match[1].match(/(v?\d+(\.\d+)+)/i);
          if (versionMatch) {
            if (currentVersion && !result[currentVersion]) {
              result[currentVersion] = currentLog.trim();
            }

            currentVersion = versionMatch[0];
            currentLog = line + '\n';

            if (!versionOrder.includes(currentVersion)) {
              versionOrder.push(currentVersion);
            }
          }
        } else if (line.includes('<!--MULTI_VERSION:')) {
          const match = line.match(/<!--MULTI_VERSION:(.*?)-->/);
          if (match && currentVersion) {
            const versions = match[1].split(',').map(v => v.trim());
            if (versions.length > 1) {
              const isEnglish = i18n.locale === 'en';
              multiVersionMap[currentVersion] = versions.map(id => ({
                id,
                name: isEnglish ? (id.endsWith('net') ? 'online' : 'offline') : (id.endsWith('net') ? '网络版' : '单机版'),
                suffix: id.endsWith('net') ? 'net' : ''
              }));
            }
          }
          currentLog += line + '\n';
        } else if (currentVersion) {
          currentLog += line + '\n';
        }
      });

      if (currentVersion && !result[currentVersion]) {
        result[currentVersion] = currentLog.trim();
      }

      // 对版本号进行排序，确保最新版本在前面
      const sortedVersions = versionOrder.sort((a, b) => {
        const aParts = a.replace(/^v/, '').split('.').map(Number);
        const bParts = b.replace(/^v/, '').split('.').map(Number);
        
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aVal = aParts[i] || 0;
          const bVal = bParts[i] || 0;
          
          if (aVal !== bVal) {
            return bVal - aVal;
          }
        }
        
        return 0;
      });

      return {
        versions: sortedVersions,
        logMap: result,
        multiVersionMap
      };
    },

    hasSubVersions(version) {
      return this.multiVersionMap[version] && this.multiVersionMap[version].length > 0;
    },

    // 判断是否显示两个下载按钮（单机版和网络版）
    shouldShowTwoButtons(version) {
      // 直接使用hasSubVersions方法判断，不硬编码特定版本
      return this.hasSubVersions(version);
    },

    // 切换升级日志的显示和隐藏
    toggleChangelog(version) {
      this.$set(this.isChangelogVisible, version, !this.isChangelogVisible[version]);
    },

    handleDownloadClick(version) {
      if (this.hasSubVersions(version)) {
        this.showDropdown = !this.showDropdown;
      } else {
        this.downloadFile(version);
      }
    },

    handleDownloadDefaultWithOption(version, option) {
      this.showDropdown = false;
      const cleanVersion = version.replace(/[^\d]/g, '');
      const suffix = option.suffix ? option.suffix : '';
      // 根据当前语言选择不同的文件名前缀
      const prefix = i18n.locale === 'en' ? 'MrtEnSetups' : 'MrtSetups';
      const filename = `${prefix}${cleanVersion}${suffix}.zip`;
      // 使用Vue推荐的方式处理外部链接
      const link = document.createElement('a');
      link.href = `${this.downloadBaseUrl}${filename}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    
    handleDownloadWithOption(version, option) {
      const cleanVersion = version.replace(/[^\d]/g, '');
      const suffix = option.suffix ? option.suffix : '';
      // 根据当前语言选择不同的文件名前缀
      const prefix = i18n.locale === 'en' ? 'MrtEnSetups' : 'MrtSetups';
      const filename = `${prefix}${cleanVersion}${suffix}.zip`;
      // 使用Vue推荐的方式处理外部链接
      const link = document.createElement('a');
      link.href = `${this.downloadBaseUrl}${filename}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    expandHistoryCard(version) {
      this.expandedVersion = version;
      this.expandedChangelog = this.changelogMap[version] || '暂无日志';
      this.expandedShowDropdown = false;
    },

    closeExpandedCard() {
      this.expandedVersion = null;
      this.expandedChangelog = '';
      this.expandedShowDropdown = false;
    },

    handleExpandedDownloadClick(version) {
      if (this.hasSubVersions(version)) {
        this.expandedShowDropdown = !this.expandedShowDropdown;
      } else {
        this.downloadFile(version);
      }
    },

    handleExpandedDownloadWithOption(version, option) {
      this.expandedShowDropdown = false;
      const cleanVersion = version.replace(/[^\d]/g, '');
      const suffix = option.suffix ? `_${option.suffix}` : '';
      // 根据当前语言选择不同的文件名前缀
      const prefix = i18n.locale === 'en' ? 'MrtEnSetups' : 'MrtSetups';
      const filename = `${prefix}${cleanVersion}${suffix}.zip`;
      // 使用Vue推荐的方式处理外部链接
      const link = document.createElement('a');
      link.href = `${this.downloadBaseUrl}${filename}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    downloadFile(version) {
      const cleanVersion = version.replace(/[^\d]/g, '');
      // 根据当前语言选择不同的文件名前缀
      const prefix = i18n.locale === 'en' ? 'MrtEnSetups' : 'MrtSetups';
      const filename = `${prefix}${cleanVersion}.zip`;
      // 使用Vue推荐的方式处理外部链接
      const link = document.createElement('a');
      link.href = `${this.downloadBaseUrl}${filename}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    
    downloadDriver(driverType, option = '') {
      // 根据不同的驱动类型和选项处理下载逻辑
      console.log(`准备下载驱动: ${driverType}${option ? ` (${option})` : ''}`);
      
      let driverUrl = '';
      let driverName = '';
      
      switch(driverType) {
        case 'PL2303':
          driverUrl = 'https://info.mrtlab.com/download/PL23XX_Prolific_DriverInstaller_v4210.zip';
          driverName = 'PL2303驱动';
          break;
        case 'CP210x':
          if (option === 'WinXP') {
            driverUrl = 'https://info.mrtlab.com/download/cp210x_winxp.rar';
            driverName = 'CP210x驱动(WinXP)';
          } else {
            driverUrl = 'https://info.mrtlab.com/download/cp210x.rar';
            driverName = 'CP210x驱动(Win7及以上)';
          }
          break;
        case 'SATA':
          if (option === 'CH341SER') {
            driverUrl = 'https://info.mrtlab.com/download/CH341SER_Driver.exe';
            driverName = 'CH341SER驱动';
          } else {
            driverUrl = 'https://info.mrtlab.com/download/MRTChangeSignal_V5.0.exe';
            driverName = 'MRTChangeSignal工具';
          }
          break;
        default:
          console.error('未知的驱动类型');
          alert('未找到该驱动的下载链接');
          return;
      }
      
      // 显示下载提示
      alert(`即将开始下载 ${driverName}`);
      // 跳转到真实的下载地址
      console.log(`驱动下载地址: ${driverUrl}`);
      // 使用Vue推荐的方式处理外部链接
      const link = document.createElement('a');
      link.href = driverUrl;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};
</script>

<style scoped>
/* 软件类型选择器样式 */
.software-type-selector {
  background-color: white;
  padding: 15px 0;
  position: relative;
}

.software-type-selector::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background-color: #e0e0e0;
}

.selector-container {
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  gap: 80px;
  position: relative;
}

/* 添加灰色分隔线 */
.type-btn:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 50%;
  right: -40px;
  width: 1px;
  height: 30px;
  background-color: #e0e0e0;
  transform: translateY(-50%);
  z-index: 4;
}

.type-btn {
  padding: 12px 30px;
  background-color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  color: #999;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
  overflow: visible;
}

.type-btn:hover {
  background-color: white;
  color: #1890ff;
}

.type-btn.active {
  background-color: white;
  color: #1890ff;
  position: relative;
  z-index: 1;
}

.type-btn.active::after {
  content: none;
}

/* 单独为活动状态添加底部横线 */
.type-btn.active::before {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 3px;
  background-color: #1890ff;
  z-index: 3;
}

/* 下拉菜单样式 */
.custom-download-btn {
  position: relative;
  display: inline-block;
}

.custom-download-btn button {
  padding: 10px 20px;
  background-color: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.3s;
}

.custom-download-btn button:hover {
  background-color: #40a9ff;
}

.custom-download-btn button.has-dropdown {
  padding-right: 35px;
  position: relative;
}

.custom-download-btn button.has-dropdown::after {
  content: '▼';
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  transition: transform 0.3s;
}

.custom-download-btn button.has-dropdown:hover::after {
  transform: translateY(-50%) rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  margin-top: 4px;
  padding: 0;
  list-style: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.dropdown-menu li {
  padding: 8px 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.dropdown-menu li:hover {
  background-color: #f5f5f5;
}

/* 主内容区域 */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

/* 当前版本下载卡片 */
.current-download-section {
  margin-bottom: 40px;
}

.download-card {
  background-color: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.download-layout {
  display: flex;
  justify-content: space-between;
  padding: 30px;
  gap: 40px;
}

.left-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.software-title {
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 30px;
  color: #333;
}

.download-btn {
  padding: 10px 20px;
  font-size: 16px;
}

.right-panel {
  flex: 1;
  min-width: 0;
}

.changelog-title {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 15px;
  color: #666;
}

.changelog-content {
  font-size: 14px;
  line-height: 1.8;
  color: #333;
}

.no-changelog {
  font-size: 14px;
  color: #999;
}

/* 历史版本区域 */
.history-section {
  margin-bottom: 40px;
}

.section-title {
  font-size: 20px;
  font-weight: 500;
  margin-bottom: 20px;
  color: #333;
}

.card-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.card {
  background-color: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #40a9ff;
}

.card-inner {
  padding: 20px;
  text-align: center;
}

.card-inner h3 {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 10px;
  color: #333;
}

.card-inner p {
  font-size: 14px;
  color: #999;
}

.no-history {
  font-size: 14px;
  color: #999;
  text-align: center;
  padding: 40px;
}

/* 软件内容区域 */
.software-content {
  background-color: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
}

/* 驱动卡片样式 */
.driver-card {
  background-color: white;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 25px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border: 1px solid #e8e8e8;
  text-align: left;
}

.driver-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

/* 不同驱动卡片的区分样式 */
.driver-card-pl2303 {
  border-left: 4px solid #40a9ff;
}

.driver-card-cp210x {
  border-left: 4px solid #52c41a;
}

.driver-card-sata {
  border-left: 4px solid #faad14;
}

.driver-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.driver-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  font-size: 28px;
  background-color: #f5f5f5;
}

.driver-card-pl2303 .driver-icon {
  background-color: #e6f7ff;
  color: #40a9ff;
}

.driver-card-cp210x .driver-icon {
  background-color: #f6ffed;
  color: #52c41a;
}

.driver-card-sata .driver-icon {
  background-color: #fff7e6;
  color: #faad14;
}

.driver-info {
  flex: 1;
}

.driver-title {
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #333;
}

.driver-subtitle {
  font-size: 16px;
  color: #666;
  margin: 0 0 5px 0;
  font-weight: 500;
}

.driver-compatibility {
  font-size: 14px;
  color: #999;
  margin: 0;
}

.driver-download-area {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 20px;
    padding-top: 10px;
    border-top: 1px dashed #f0f0f0;
  }

.driver-description {
  font-size: 15px;
  color: #666;
  background-color: #fafafa;
  border-radius: 8px;
  overflow: hidden;
}

/* 升级日志切换按钮 */
.changelog-toggle {
  padding: 15px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f0f0f0;
  border-bottom: 1px solid #e0e0e0;
  transition: background-color 0.3s;
}

.changelog-toggle:hover {
  background-color: #e8e8e8;
}

.toggle-icon {
  transition: transform 0.3s;
  font-size: 12px;
}

/* 升级日志容器 */
.changelog-container {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
}

.changelog-container.expanded {
  max-height: 500px;
}

/* 支持鼠标滚轮的升级日志内容区域 */
.changelog-content-scrollable {
  padding: 15px;
  line-height: 1.8;
  max-height: 450px;
  overflow-y: auto;
  /* 自定义滚动条样式 */
}

.changelog-content-scrollable::-webkit-scrollbar {
  width: 6px;
}

.changelog-content-scrollable::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.changelog-content-scrollable::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.changelog-content-scrollable::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 自定义下载按钮样式 */
.custom-download-btn button {
  padding: 12px 30px;
  font-size: 16px;
  font-weight: 500;
  color: white;
  background-color: #40a9ff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.custom-download-btn button:hover {
  background-color: #69c0ff;
  transform: translateY(-1px);
}

.driver-card-cp210x .custom-download-btn button {
  background-color: #52c41a;
}

.driver-card-cp210x .custom-download-btn button:hover {
  background-color: #73d13d;
}

.driver-card-sata .custom-download-btn button {
  background-color: #faad14;
}

.driver-card-sata .custom-download-btn button:hover {
  background-color: #ffc53d;
}

.software-content h2 {
  font-size: 24px;
  font-weight: 500;
  margin-bottom: 20px;
  color: #333;
}

.software-content p {
  font-size: 16px;
  color: #999;
}

/* 弹窗样式 */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.overlay-content {
  background-color: white;
  border-radius: 8px;
  max-width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 20px;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  z-index: 10;
}

.close-btn:hover {
  color: #333;
}

.overlay-download-content {
  display: flex;
  justify-content: space-between;
  padding: 40px;
  gap: 40px;
}
</style>



// WEBPACK FOOTER //
// src/components/softwareList.vue